import {exists, localStorageGetParsedItem} from '@aboutyou/utils';
import React, {useCallback, useEffect} from 'react';
import {getIsConsentBannerAccepted} from '../utils/consentBannerAcceptedState';
import {
  EMPTY_VALUE,
  useAddKeyToIgnoreUpdatesOnOtherTabsList,
  useBaseStore,
  useStoredValue,
  useUpdateStoreValue,
} from './LocalStorageContext';

export function useLocalStorage<Value>({
  key,
  defaultValue,
  fallbackValue = defaultValue,
  ignoreUpdatesOutsideCurrentTab = false,
  forceStoreDefaultValuesToLocalStorage = false,
  forcePersistValue_ILLEGALLY = false,
  onInitialSyncFromStorage,
}: {
  key: string;
  /** Default value is used when no value exists in browser's local storage */
  defaultValue: Value;
  /** Fallback value is used before hydration is done and on the server */
  fallbackValue?: Value;
  /** Ignore updates to localStorage outside current tab. eg: in another tab or window */
  ignoreUpdatesOutsideCurrentTab?: boolean;
  /** Setting this to true makes it force the updates written to localStorage, even if the value is same as the default value */
  forceStoreDefaultValuesToLocalStorage?: boolean;
  /** Setting this to true will force save the value in localStorage even when there is no cookie consent for doing so.
   * This is in general a legal threat and can only be used if agreed with the legal department.
   * It will be mandatory to adjust the consent banner text for all countries before the change can go online.
   * Please use this only if it's strictly mandatory to make the ABOUT YOU website work
   */
  forcePersistValue_ILLEGALLY?: boolean;
  onInitialSyncFromStorage?: (
    nextValue: Value,
    initialValue: Value | undefined,
  ) => void;
}) {
  if (!process.browser) {
    return [
      fallbackValue,
      (
        _value: (Value | undefined) | ((oldValue: Value | undefined) => Value),
      ) => void 0,
    ] as const;
  }

  /* eslint-disable react-hooks/rules-of-hooks */
  const storeValue = useStoredValue(key) as Value | undefined;
  const updateStoreValue = useUpdateStoreValue();
  const addKeyToIgnoreUpdatesOnOtherTabs =
    useAddKeyToIgnoreUpdatesOnOtherTabsList();

  const value: Value | undefined =
    storeValue === EMPTY_VALUE ? undefined : storeValue ?? defaultValue;

  const updateCallback = React.useCallback(
    (
      stateOrSetStateCallback:
        | (Value | undefined)
        | ((oldValue: Value | undefined) => Value),
    ) => {
      updateStoreValue({
        key,
        nextValue: stateOrSetStateCallback,
        forceStoreDefaultValuesToLocalStorage,
        forcePersistValue_ILLEGALLY,
      });
    },
    [
      forcePersistValue_ILLEGALLY,
      forceStoreDefaultValuesToLocalStorage,
      key,
      updateStoreValue,
    ],
  );

  useEffect(() => {
    if (!forcePersistValue_ILLEGALLY && !getIsConsentBannerAccepted()) return; // do not read from localStorage if the user has not accepted the cookie banner

    const valueFromLocalStorage = localStorageGetParsedItem(key);

    if (
      exists(valueFromLocalStorage) &&
      JSON.stringify(valueFromLocalStorage) !== JSON.stringify(value)
    ) {
      // eslint-disable-next-line local-rules/set-state-in-use-effect -- we want to update the state here once on mount to update the value from localStorage which is not available on the server
      updateCallback(valueFromLocalStorage);
      onInitialSyncFromStorage?.(valueFromLocalStorage, value);
    }
  }, []);

  // keep track of keys where updates on other tabs are ignored, so that we can ignore updates on other tabs for those keys
  useEffect(() => {
    if (ignoreUpdatesOutsideCurrentTab) {
      addKeyToIgnoreUpdatesOnOtherTabs(key);
    }
  }, [addKeyToIgnoreUpdatesOnOtherTabs, ignoreUpdatesOutsideCurrentTab, key]);

  return [value, updateCallback] as const;
}

const _readValues = new Map();
/**
 * This hook can be used to read the value from store with a fallback to reading directly from localStorage.
 * Use this hook only if you need to read the value in a callback event (ex: Button Click) and not in a render function.
 * Use the regular `useLocalStorage` hook if the value is affects what is rendered.
 *
 * This hooks keeps a cache of the value read from localStorage (in _readValues), so that it doesn't need to read from localStorage multiple times.
 */
export const useLocalStorageValueCallback = <Value>({
  key,
  defaultValue,
}: {
  key: string;
  defaultValue: Value;
}) => {
  const baseStore = useBaseStore();

  return useCallback(() => {
    if (!process.browser) {
      throw new Error(
        'useLocalStorageValueCallback can only be used in the browser in callbacks. Never on the server during render time!',
      );
    }

    const storeState = baseStore.getState().store[key];
    const storeValue = (storeState?.valueAfterCookieConsent ??
      storeState?.value) as Value;
    if (storeState) {
      return storeValue === EMPTY_VALUE
        ? undefined
        : storeValue ?? defaultValue;
    }

    if (_readValues.has(key)) {
      return _readValues.get(key) as Value;
    }

    const valueFromLocalStorage = localStorageGetParsedItem(key) as Value;
    _readValues.set(key, valueFromLocalStorage);

    return (_readValues.get(key) as Value) ?? defaultValue;
  }, [baseStore, defaultValue, key]);
};

/**
 * Use this hook if you only need to update the value and you don't need the updated value.
 * This hook helps to avoid unnecessary re-renders due to the updated value when you only need to update the value.
 */
export const useLocalStorageUpdateCallback = <Value>({key}: {key: string}) => {
  const updateStoreValue = useUpdateStoreValue();

  return useCallback(
    (value: Value | ((oldValue: Value | undefined) => Value)) => {
      updateStoreValue({
        key,
        nextValue: value,
      });
    },
    [key, updateStoreValue],
  );
};
